import { Dialog } from "@headlessui/react";
import React, { useState } from "react";
import Input from "../../../components/controls/Input";
import Button from "../../../components/ui/Button";
import { lastNine } from "../employeeProductionHelpers";
import {
  ReceivingItem,
  UpdateScannedItemPayload,
} from "../employeeProductionTypes";

type Props = {
  item: ReceivingItem;
  productionId: number;
  onConfirm: (payload: UpdateScannedItemPayload) => Promise<void>;
  onClose: () => void;
};

export function EditScannedItemDialog({
  item,
  productionId,
  onConfirm,
  onClose,
}: Props) {
  const [amount, setAmount] = useState(String(item.ingredient_amount_used));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const finalAmount = Number(amount);
    if (!Number.isFinite(finalAmount) || finalAmount < 0) {
      setError("Amount must be greater than or equal to 0");
      return;
    }
    if (finalAmount > item.received_amount) {
      setError(
        `Amount cannot exceed ${item.received_amount.toFixed(2)} ${item.unit_of_measure ?? ""}`,
      );
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm({
        production_id: productionId,
        barcode: item.barcode,
        amount_used: finalAmount,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Dialog.Title className="mt-3 text-center text-medium font-bold leading-6 text-gray-900 sm:mt-5">
        Barcode: {lastNine(item.barcode)}
      </Dialog.Title>
      <div className="mt-4 space-y-4 text-left">
        <Input
          label="Available Amount"
          name="in_stock"
          type="number"
          value={item.received_amount}
          disabled={true}
          labelOn={true}
          suffix={item.unit_of_measure ?? ""}
        />
        <Input
          label="Amount in Use"
          name="ingredient_amount_used"
          type="number"
          step="0.01"
          min="0"
          max={item.received_amount}
          value={amount}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setAmount(event.target.value);
            setError("");
          }}
          labelOn={true}
          suffix={item.unit_of_measure ?? ""}
        />
        {error && <p className="text-sm font-semibold text-red-500">{error}</p>}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-6">
        <Button
          variant="secondary"
          fullWidth
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button fullWidth onClick={submit} loading={submitting}>
          Confirm
        </Button>
      </div>
    </div>
  );
}
